import * as maplibregl from "maplibre-gl";
import type { GeoJSONSource, Map as MlMap, MapGeoJSONFeature } from "maplibre-gl";
import type { AppConfig, Level, MapData, Metric } from "./api.ts";
import { computeStops, fillExpression } from "./colors.ts";
import { DATA_CREDIT_HTML } from "../shared/format.ts";
import { BAY_AREA_BOUNDS, DEEP_STATE, type Bounds } from "../shared/regions.ts";

const SRC = "b";
const LABELS = "labels";
// a folder per MapLibre version (web/build.ts): cached for good, and the worker shares main.js's copy of the shared half
maplibregl.setWorkerUrl(`/assets/maplibre/${maplibregl.getVersion()}/maplibre-gl-worker.mjs`);
const TILE_LEVELS: Array<Exclude<Level, "us">> = ["state", "metro", "county", "zip", "city"];
/** Tile layers only exist from these zooms; below them the map asks the user to zoom in. */
export const MIN_ZOOM: Record<Level, number> = { us: 0, state: 0, metro: 2, county: 3, zip: 6, city: 5 };
/** The opening views: the Bay Area (the front door, M12: at state zoom California's cities are too small to read),
 *  the deep state (scope "ca", shared/regions.ts) and the lower 48 */
const box = ([[w, s], [e, n]]: Bounds): [[number, number], [number, number]] => [[w, s], [e, n]];
export const VIEWS = { bay: box(BAY_AREA_BOUNDS), ca: box(DEEP_STATE.bounds), us: box([[-124.8, 24.4], [-66.9, 49.4]]) };
export type View = keyof typeof VIEWS;
// phones: room at the top and bottom for the search bar, legend and bottom bar
const viewPadding = () => innerWidth <= 600 ? { top: 170, right: 8, bottom: 150, left: 8 } : { top: 30, right: 30, bottom: 90, left: 30 };
/** Value labels appear from these zooms (dense levels need more room). */
const LABEL_ZOOM: Record<Level, number> = { us: 99, state: 3.5, metro: 5.5, county: 6.5, zip: 9, city: 8.5 };

export type ScaleMode = "view" | "national";

export interface ScaleInfo {
  /** colors low → high, from /api/map */
  colors: string[];
  stops: number[];
  /** how many areas the colors were fitted to */
  n: number;
  scope: ScaleMode;
}

export interface MapEvents {
  onHover(f: { level: Level; code: string; name: string } | null, point?: { x: number; y: number }): void;
  onClick(f: { level: Level; code: string; name: string }): void;
  onZoom(z: number): void;
  /** called whenever the color scale changes (new data, or the view moved in "view" mode) */
  onScale(s: ScaleInfo): void;
}

export interface LabelPoint {
  code: string;
  lon: number;
  lat: number;
  text: string;
}

/** A place from a ranked answer, marked on the map with its number. */
export interface HitPoint {
  code: string;
  lon: number;
  lat: number;
  title: string;
}

const NO_HITS = ["in", ["get", "code"], ["literal", []]];
/** see-through at street zoom so roads and parks show, a little stronger when zoomed out; with a screen on, areas
 *  outside it fade (zoom must be the top-level input, so the case sits inside each stop) */
const fillOpacity = (screen: boolean) => ["interpolate", ["linear"], ["zoom"],
  ...[[4, 0.8], [9, 0.6], [12, 0.5]].flatMap(([z, o]) => [z, screen ? ["case", ["boolean", ["feature-state", "m"], false], o, 0.07] : o])];

export class ChoroplethMap {
  readonly map: MlMap;
  private level: Level = "county";
  private loadedLevel?: Level;
  private hovered?: { layer: string; id: string | number };
  private selected?: { layer: string; id: string };
  private ready: Promise<void>;
  private metric?: Metric;
  private colorScale: Pick<MapData, "colors" | "stops"> = { colors: [], stops: [] };
  private values = new Map<string, number>();
  private scaleMode: ScaleMode = "view";
  private recolorTimer?: ReturnType<typeof setTimeout>;
  private hasGlyphs = false;
  private labelsOn = true;
  private hits?: { level: Exclude<Level, "us">; codes: string[]; markers: maplibregl.Marker[] };
  /** a screen's matches: everything else is faded */
  private screen?: { level: Exclude<Level, "us">; codes: string[] };

  constructor(container: HTMLElement, cfg: AppConfig, private readonly ev: MapEvents, view: View = "bay") {
    this.map = new maplibregl.Map({
      container,
      style: cfg.basemapStyle === "blank" || !cfg.basemapStyle
        ? { version: 8, sources: {}, layers: [{ id: "bg", type: "background", paint: { "background-color": "#e9eef3" } }] }
        : cfg.basemapStyle,
      // open on the Bay Area or the lower 48 as bounds, whatever the window's shape (a fixed zoom showed Canada and
      // Mexico on wide screens)
      bounds: VIEWS[view],
      fitBoundsOptions: { padding: viewPadding() },
      minZoom: 2,
      maxZoom: 14,
      // not compact: the sources (and OpenStreetMap) must be credited visibly, not behind a toggle
      attributionControl: { compact: false },
      dragRotate: false,
      pitchWithRotate: false,
    });
    // MapLibre's default handler logs only the (minified) error class; say what failed
    this.map.on("error", (e) => {
      const err = e.error as (Error & { status?: number; url?: string }) | undefined;
      console.error(`map error: ${err?.message ?? e}`, err?.status ?? "", err?.url ?? "");
    });
    this.map.touchZoomRotate.disableRotation();
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    this.map.addControl(new maplibregl.GeolocateControl({}), "bottom-right");

    this.ready = new Promise((resolve) => {
      this.map.on("load", () => {
        performance.mark("map:load");
        if (!cfg.tiles) {
          console.warn("no boundary tiles — run `bun run tiles`");
          resolve();
          return;
        }
        this.map.addSource(SRC, {
          type: "vector",
          tiles: [location.origin + cfg.tiles.url], // not new URL(): it would percent-encode {z}/{x}/{y}
          minzoom: 0,
          maxzoom: Number(cfg.tiles.maxzoom ?? 11),
          promoteId: "code",
          attribution: DATA_CREDIT_HTML,
        });
        const style = this.map.getStyle();
        // put the choropleth under the basemap's labels so place names stay readable
        const firstSymbol = style.layers.find((l) => l.type === "symbol")?.id;
        for (const lvl of TILE_LEVELS) {
          this.map.addLayer({
            id: `fill-${lvl}`, type: "fill", source: SRC, "source-layer": lvl, minzoom: MIN_ZOOM[lvl],
            layout: { visibility: "none" },
            paint: { "fill-color": "rgba(0,0,0,0)", "fill-opacity": fillOpacity(false) as never },
          }, firstSymbol);
          this.map.addLayer({
            id: `line-${lvl}`, type: "line", source: SRC, "source-layer": lvl, minzoom: MIN_ZOOM[lvl],
            layout: { visibility: "none" },
            paint: {
              "line-color": ["case", ["boolean", ["feature-state", "selected"], false], "#111827",
                ["boolean", ["feature-state", "hover"], false], "#1f2937", "rgba(40,40,60,0.45)"],
              // zoom must be the top-level input, so the hover/selected case sits inside each stop
              "line-width": ["interpolate", ["linear"], ["zoom"],
                4, ["case", ["boolean", ["feature-state", "selected"], false], 2.5, ["boolean", ["feature-state", "hover"], false], 1.8, 0.3],
                10, ["case", ["boolean", ["feature-state", "selected"], false], 2.5, ["boolean", ["feature-state", "hover"], false], 1.8, 0.8]],
            },
          }, firstSymbol);
        }
        // the places of a ranked answer: a white casing under a dark line, so the outline reads on any fill color.
        // A filter, not feature-state: reloading a level's values clears feature-state
        for (const lvl of TILE_LEVELS) {
          this.map.addLayer({
            id: `hit-casing-${lvl}`, type: "line", source: SRC, "source-layer": lvl, filter: NO_HITS as never,
            paint: { "line-color": "#ffffff", "line-width": 6, "line-opacity": 0.9 },
          }, firstSymbol);
          this.map.addLayer({
            id: `hit-${lvl}`, type: "line", source: SRC, "source-layer": lvl, filter: NO_HITS as never,
            paint: { "line-color": "#0b0f19", "line-width": 3 },
          }, firstSymbol);
        }
        this.map.addLayer({
          id: "state-borders", type: "line", source: SRC, "source-layer": "state",
          paint: { "line-color": "rgba(40,40,40,0.55)", "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.6, 8, 1.4] },
        }, firstSymbol);

        // value labels ("94941 / $2,131,868"): only when the basemap ships fonts
        this.hasGlyphs = !!style.glyphs;
        if (this.hasGlyphs) {
          const baseFont = style.layers
            .map((l) => (l.type === "symbol" ? (l.layout as Record<string, unknown> | undefined)?.["text-font"] : undefined))
            .find((f): f is string[] => Array.isArray(f) && f.every((x) => typeof x === "string"));
          const bold = baseFont?.map((f) => f.replace(/Regular|Italic/, "Bold")) ?? ["Noto Sans Bold"];
          this.map.addSource(LABELS, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
          this.map.addLayer({
            id: LABELS, type: "symbol", source: LABELS, minzoom: LABEL_ZOOM.county,
            layout: {
              "text-field": ["get", "t"],
              "text-font": bold,
              "text-size": ["interpolate", ["linear"], ["zoom"], 6, 10, 10, 12, 13, 14],
              "text-line-height": 1.15,
              "text-allow-overlap": false,
              "text-padding": 2,
              "symbol-sort-key": ["get", "rank"],
            },
            paint: { "text-color": "#1c2433", "text-halo-color": "rgba(255,255,255,0.85)", "text-halo-width": 1.4 },
          });
        }
        this.bindEvents();
        resolve();
      });
    });
  }

  whenReady() {
    return this.ready;
  }

  private tileLevel(): Exclude<Level, "us"> {
    return this.level === "us" ? "state" : this.level;
  }

  private bindEvents() {
    const layers = TILE_LEVELS.map((l) => `fill-${l}`);
    const visibleLayers = () => layers.filter((l) => this.map.getLayoutProperty(l, "visibility") === "visible");
    this.map.on("mousemove", (e) => {
      const f = this.map.queryRenderedFeatures(e.point, { layers: visibleLayers() })[0];
      this.setHover(f);
      this.map.getCanvas().style.cursor = f ? "pointer" : "";
      this.ev.onHover(f ? this.describe(f) : null, e.point);
    });
    this.map.on("mouseout", () => {
      this.setHover(undefined);
      this.ev.onHover(null);
    });
    this.map.on("click", (e) => {
      const f = this.map.queryRenderedFeatures(e.point, { layers: visibleLayers() })[0];
      if (f) this.ev.onClick(this.describe(f));
    });
    this.map.on("zoomend", () => this.ev.onZoom(this.map.getZoom()));
    // re-fit the colors to what's on screen after every pan/zoom
    this.map.on("moveend", () => this.scheduleRecolor());
  }

  private describe(f: MapGeoJSONFeature) {
    const lvl = f.sourceLayer as Level;
    return { level: lvl, code: String(f.properties.code), name: String(f.properties.name) };
  }

  private setHover(f?: MapGeoJSONFeature) {
    if (this.hovered) this.map.setFeatureState({ source: SRC, sourceLayer: this.hovered.layer, id: this.hovered.id }, { hover: false });
    this.hovered = f && f.id != null ? { layer: f.sourceLayer!, id: f.id } : undefined;
    if (this.hovered) this.map.setFeatureState({ source: SRC, sourceLayer: this.hovered.layer, id: this.hovered.id }, { hover: true });
  }

  /** Outline one area (or none). Before the map has loaded this only remembers it; show() draws it. */
  select(level: Level | null, code?: string) {
    const ready = !!this.map.getSource(SRC);
    if (ready && this.selected) this.map.setFeatureState({ source: SRC, sourceLayer: this.selected.layer, id: this.selected.id }, { selected: false });
    this.selected = level && code && level !== "us" ? { layer: level, id: code } : undefined;
    if (ready && this.selected) this.map.setFeatureState({ source: SRC, sourceLayer: this.selected.layer, id: this.selected.id }, { selected: true });
  }

  setScaleMode(mode: ScaleMode) {
    this.scaleMode = mode;
    this.recolor();
  }

  /** Show one level and color it with the given values. */
  async show(level: Level, metric: Metric, data: MapData) {
    await this.ready;
    if (!this.map.getSource(SRC)) return;
    performance.mark("map:show");
    this.level = level;
    this.metric = metric;
    this.colorScale = { colors: data.colors, stops: data.stops };
    const tl = this.tileLevel();
    for (const l of TILE_LEVELS) {
      const vis = l === tl ? "visible" : "none";
      this.map.setLayoutProperty(`fill-${l}`, "visibility", vis);
      this.map.setLayoutProperty(`line-${l}`, "visibility", vis);
    }
    if (this.loadedLevel) this.map.removeFeatureState({ source: SRC, sourceLayer: this.loadedLevel });
    this.values.clear();
    if (level === "us") {
      // national view: paint every state with the single US value
      const v = data.values[0] ?? null;
      for (const s of ["01","02","04","05","06","08","09","10","11","12","13","15","16","17","18","19","20","21","22","23","24","25","26","27","28","29","30","31","32","33","34","35","36","37","38","39","40","41","42","44","45","46","47","48","49","50","51","53","54","55","56","72"]) {
        this.map.setFeatureState({ source: SRC, sourceLayer: "state", id: s }, { v });
        if (v != null) this.values.set(s, v);
      }
    } else {
      for (let i = 0; i < data.codes.length; i++) {
        const v = data.values[i];
        this.map.setFeatureState({ source: SRC, sourceLayer: tl, id: data.codes[i]! }, { v });
        if (v != null) this.values.set(data.codes[i]!, v);
      }
    }
    this.loadedLevel = tl;
    this.markScreen();
    if (this.hits && this.hits.level !== tl) this.clearHits(); // the marked places belong to another level
    if (this.selected) this.map.setFeatureState({ source: SRC, sourceLayer: this.selected.layer, id: this.selected.id }, { selected: true });
    if (this.map.getLayer(LABELS)) this.map.setLayerZoomRange(LABELS, LABEL_ZOOM[level], 24);
    // national colors right away, then re-fit to the view once the new level's tiles are drawn
    this.recolor("national");
    performance.mark("map:painted");
    this.map.once("idle", () => { performance.mark("map:idle"); this.recolor(); });
  }

  /** Drop the shown values (a failed load): the areas go uncolored and a pan has nothing to re-fit the colors to. */
  clear() {
    this.metric = undefined;
    this.values.clear();
    if (!this.map.getSource(SRC) || !this.loadedLevel) return;
    this.map.removeFeatureState({ source: SRC, sourceLayer: this.loadedLevel });
    if (this.selected) this.map.setFeatureState({ source: SRC, sourceLayer: this.selected.layer, id: this.selected.id }, { selected: true });
    this.markScreen();
  }

  /** Label points ("code\nvalue") for the current level; empty array clears them. */
  setLabels(points: LabelPoint[]) {
    const src = this.map.getSource(LABELS) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: "FeatureCollection",
      features: this.labelsOn
        ? points.map((p, i) => ({ type: "Feature", properties: { t: p.text, rank: i }, geometry: { type: "Point", coordinates: [p.lon, p.lat] } }))
        : [],
    });
  }

  setLabelsOn(on: boolean) {
    this.labelsOn = on;
    if (this.map.getLayer(LABELS)) this.map.setLayoutProperty(LABELS, "visibility", on ? "visible" : "none");
  }

  get labelsAvailable() {
    return this.hasGlyphs;
  }

  private scheduleRecolor() {
    clearTimeout(this.recolorTimer);
    this.recolorTimer = setTimeout(() => this.recolor(), 120);
  }

  /** Fit the color stops to the areas on screen (or the whole level) and repaint. */
  private recolor(force?: ScaleMode) {
    const m = this.metric;
    if (!m || !this.map.getSource(SRC)) return;
    const tl = this.tileLevel();
    let scope: ScaleMode = force ?? this.scaleMode;
    let vals: number[] = [];
    if (scope === "view" && this.level !== "us" && this.map.getZoom() >= MIN_ZOOM[this.level]) {
      const seen = new Set<string>();
      for (const f of this.map.queryRenderedFeatures({ layers: [`fill-${tl}`] })) {
        const code = String(f.id ?? f.properties.code);
        if (seen.has(code)) continue;
        seen.add(code);
        const v = this.values.get(code);
        if (v != null) vals.push(v);
      }
    }
    let stops: number[];
    if (vals.length < 8) {
      scope = "national";
      stops = this.colorScale.stops; // the server's, fitted to every area at the level
      vals = [...this.values.values()];
    } else stops = computeStops(m, vals);
    const { colors } = this.colorScale;
    this.map.setPaintProperty(`fill-${tl}`, "fill-color", fillExpression(colors, stops) as never);
    this.ev.onScale({ colors, stops, n: vals.length, scope });
  }

  /**
   * Mark the places of a ranked answer: an outline on each area and a numbered pin at its center, and fit the view
   * to them. The pins report clicks and hovers by index.
   */
  setHits(level: Exclude<Level, "us">, points: HitPoint[], ev: { onClick(i: number): void; onHover(i: number | null): void },
    /** `first`: the first pin's number (a later page of a list); `pad`: room for a list docked over an edge of the map */
    o: { first?: number; pad?: Partial<Record<"top" | "left" | "right" | "bottom", number>> } = {}) {
    this.clearHits();
    if (!this.map.getSource(SRC) || !points.length) return;
    const codes = points.map((p) => p.code);
    const filter = ["in", ["get", "code"], ["literal", codes]];
    this.map.setFilter(`hit-casing-${level}`, filter as never);
    this.map.setFilter(`hit-${level}`, filter as never);
    const markers = points.map((p, i) => {
      const pin = document.createElement("button");
      pin.className = "hit-pin";
      const n = (o.first ?? 1) + i;
      pin.textContent = String(n);
      pin.title = p.title;
      pin.setAttribute("aria-label", `${n}: ${p.title}`);
      pin.addEventListener("click", (e) => { e.stopPropagation(); ev.onClick(i); });
      pin.addEventListener("mouseenter", () => ev.onHover(i));
      pin.addEventListener("mouseleave", () => ev.onHover(null));
      // just above the center, where the value label sits
      return new maplibregl.Marker({ element: pin, anchor: "bottom", offset: [0, -16] }).setLngLat([p.lon, p.lat]).addTo(this.map);
    });
    this.hits = { level, codes, markers };
    const b = new maplibregl.LngLatBounds();
    for (const p of points) b.extend([p.lon, p.lat]);
    // room for the area shapes around their centers, the legend (bottom right) and the bottom bar
    this.map.fitBounds(b, { padding: { top: 120, left: 140, right: 160, bottom: 180, ...o.pad }, maxZoom: { zip: 10.5, city: 10, county: 8, metro: 7, state: 5 }[level], speed: 1.6 });
  }

  /** Fade every area at `level` but these (a screen's matches); null clears. */
  setScreen(level: Exclude<Level, "us">, codes: string[] | null) {
    this.screen = codes ? { level, codes } : undefined;
    void this.ready.then(() => this.markScreen());
  }

  private markScreen() {
    if (!this.map.getSource(SRC)) return;
    const tl = this.tileLevel();
    const on = !!this.screen && this.screen.level === tl && this.level !== "us";
    for (const l of TILE_LEVELS) this.map.setPaintProperty(`fill-${l}`, "fill-opacity", fillOpacity(on && l === tl) as never);
    if (!on) return;
    // show() clears feature-state on a level change, and a new screen replaces the old marks
    const prev = this.screenMarked;
    const next = new Set(this.screen!.codes);
    if (prev?.level === tl) for (const c of prev.codes) if (!next.has(c)) this.map.setFeatureState({ source: SRC, sourceLayer: tl, id: c }, { m: false });
    for (const c of next) this.map.setFeatureState({ source: SRC, sourceLayer: tl, id: c }, { m: true });
    this.screenMarked = { level: tl, codes: next };
  }
  private screenMarked?: { level: Exclude<Level, "us">; codes: Set<string> };

  /** Emphasize one marked place (a hover on its row or pin), or none. */
  focusHit(code: string | null) {
    if (!this.hits) return;
    const lvl = this.hits.level;
    const on = ["==", ["get", "code"], code ?? ""];
    this.map.setPaintProperty(`hit-casing-${lvl}`, "line-width", ["case", on, 9, 6] as never);
    this.map.setPaintProperty(`hit-${lvl}`, "line-width", ["case", on, 5, 3] as never);
    this.hits.markers.forEach((m, i) => m.getElement().classList.toggle("focus", this.hits!.codes[i] === code));
  }

  clearHits() {
    if (!this.hits) return;
    for (const m of this.hits.markers) m.remove();
    this.map.setFilter(`hit-casing-${this.hits.level}`, NO_HITS as never);
    this.map.setFilter(`hit-${this.hits.level}`, NO_HITS as never);
    this.hits = undefined;
  }

  /** Whether an area is one of the marked places (links a hover on the map to its row). */
  isHit(level: Level, code: string) {
    return this.hits?.level === level && this.hits.codes.includes(code);
  }

  flyTo(lon: number, lat: number, level: Level) {
    const z: Record<Level, number> = { us: 3.4, state: 5.2, metro: 7.2, county: 8, zip: 10.5, city: 9.5 };
    const to = { center: [lon, lat] as [number, number], zoom: Math.max(this.map.getZoom(), z[level]) };
    // before the first load (an area link) start there instead of flying across a map that isn't drawn yet
    if (this.map.loaded()) this.map.flyTo({ ...to, speed: 1.6 });
    else this.map.jumpTo(to);
  }

  zoom() {
    return this.map.getZoom();
  }

  zoomTo(z: number) {
    this.map.easeTo({ zoom: z });
  }

  showView(view: View) {
    this.map.fitBounds(VIEWS[view], { padding: viewPadding(), speed: 1.6 });
  }
}
